import { useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

/**
 * Custom hook providing CSV and PDF export functionality for operations.
 *
 * @param {Array}  filteredOperations  Currently visible (filtered) operations
 * @param {Set}    selectedOperations  Set of selected operation ids
 * @param {Object} dateFilters         { startDate, endDate }
 * @returns {{ exportToCSV: Function, exportToPDF: Function }}
 */
export function useOperationExport(filteredOperations, selectedOperations, dateFilters) {
  const getSelectedOps = useCallback(() => {
    const ops = filteredOperations.filter((op) => selectedOperations.has(op.id));
    if (ops.length === 0) {
      toast.error('Najpierw wybierz operacje do eksportu uzywajac checkboxow');
    }
    return ops;
  }, [filteredOperations, selectedOperations]);

  // -------- CSV --------
  const exportToCSV = useCallback(() => {
    const selectedOps = getSelectedOps();
    if (selectedOps.length === 0) return;

    const sanitize = (text) => {
      if (!text) return '';
      return text.toString().replace(/"/g, '""');
    };

    const headers = ['Data', 'Typ operacji', 'Typ platnosci', 'Nr dokumentu', 'Kontrahent', 'Opis', 'Kwota'];
    const csvContent = [
      headers.join(','),
      ...selectedOps.map((op) =>
        [
          sanitize(op.data_operacji),
          sanitize(op.typ_operacji),
          sanitize(op.typ_platnosci),
          `"${sanitize(op.numer_dokumentu)}"`,
          `"${sanitize(op.kontrahent)}"`,
          `"${sanitize(op.opis)}"`,
          op.kwota.toFixed(2)
        ].join(',')
      )
    ].join('\n');

    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `operacje_kasa_bank_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [getSelectedOps]);

  // -------- PDF (backend) --------
  const exportToPDF = useCallback(async () => {
    const selectedOps = getSelectedOps();
    if (selectedOps.length === 0) return;

    try {
      const response = await api.post('/kasa-bank/operations/export-pdf', {
        operation_ids: selectedOps.map((op) => op.id),
        date_from: dateFilters.startDate || null,
        date_to: dateFilters.endDate || null
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `operacje_kasa_bank_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Blad podczas generowania PDF:', error);
      toast.error('Wystapil blad podczas generowania PDF. Sprobuj ponownie.');
    }
  }, [getSelectedOps, dateFilters]);

  return { exportToCSV, exportToPDF };
}
